// components/CustomOrganizationSwitcher.tsx
import React, { useState } from "react";
import { Settings } from "lucide-react";

import { useSession } from "@/config/auth";
import { useOrganization } from '@/context/OrganizationContext';

import { OrganizationModal } from "@/components/inbox/components/toolbar/OrganizationModal";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const CustomOrganizationSwitcher: React.FC = () => {
  const { data: sessionData } = useSession();
  const { activeOrg, orgPending: isPending } = useOrganization();
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const organizationId = sessionData?.session?.activeOrganizationId;

  // Still loading or no org yet → render nothing
  if (!organizationId || isPending) return null;
  if (!activeOrg) return null;

  return (
    <>
      <div className="bg-gradient-to-r from-background/95 to-muted/40 border border-border/40 rounded-lg transition-all duration-300 hover:border-border/60 hover:shadow-sm backdrop-blur-sm">
        <div className="flex items-center gap-2 p-2">
          {/* Organization Avatar */}
          <div className="relative">
            <Avatar className="h-8 w-8 ring-1 ring-primary/20 transition-all duration-300 hover:ring-primary/40">
              <AvatarImage
                src={activeOrg.logo || undefined}
                alt={activeOrg.name}
                className="object-cover"
              />
              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground font-medium text-xs">
                {activeOrg.name?.charAt(0)?.toUpperCase() ?? "W"}
              </AvatarFallback>
            </Avatar>

            {/* Status Indicator */}
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-background bg-emerald-500" />
          </div>

          {/* Organization Info */}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-xs text-foreground leading-tight truncate">
              {activeOrg.name}
            </div>
            <div className="text-[10px] text-muted-foreground/70 truncate">
              Workspace
            </div>
          </div>

          {/* Settings button */}
          <button
            onClick={() => setIsProfileOpen(true)}
            className={cn(
              "p-1.5 rounded-md transition-all duration-200",
              "hover:bg-muted/50 hover:scale-105",
              "active:scale-95",
              "group"
            )}
            aria-label="Manage organization"
          >
            <Settings className="w-3.5 h-3.5 text-muted-foreground/70 group-hover:text-foreground transition-colors" />
          </button>
        </div>
      </div>

      {/* Pass the active organization into modal */}
      <OrganizationModal
        open={isProfileOpen}
        onOpenChange={setIsProfileOpen}
      />
    </>
  );
};

export default CustomOrganizationSwitcher;
